import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import DashboardCard from './DashboardCard';
import { CATEGORY_FOLD_COLOR, PROJECT_TOP_N, formatTry, projectLabel } from '../constants';
import { LegendItem } from '../styles';

// Proje × kategori — en çok harcayan 5 projede harcamanın kategorilere dağılımı (%100 yığılmış
// yatay çubuk). Kategori renkleri panonun diğer kartlarıyla AYNI harita (categoryColors).

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 8px;
`;

const Swatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 6px;
  background: ${({ $color }) => $color};
`;

/// { rows: [{ name, [categoryId]: pay%, __tutar_[categoryId]: ₺ }], categories: [{ id, name }] } (saf).
export function buildMixModel(data, t) {
  if (!data) return { rows: [], categories: [] };
  const top = (data.items || [])
    .filter((i) => i.projectId && i.windowTotal > 0)
    .sort((a, b) => b.windowTotal - a.windowTotal)
    .slice(0, PROJECT_TOP_N);

  const kategoriler = new Map();
  const rows = top.map((p) => {
    const hucreler = (data.categoryMix || []).filter((c) => c.projectId === p.projectId);
    const toplam = hucreler.reduce((s, c) => s + c.total, 0);
    const row = { projectId: p.projectId, name: projectLabel(p, t), total: toplam };
    hucreler.forEach((c) => {
      kategoriler.set(c.categoryId, c.categoryName);
      row[c.categoryId] = toplam > 0 ? Math.round((c.total * 1000) / toplam) / 10 : 0;
      row[`__tutar_${c.categoryId}`] = c.total;
    });
    return row;
  });

  return {
    rows,
    categories: [...kategoriler.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')),
  };
}

export default function ProjectCategoryMixCard({ slice, categoryColors }) {
  const { t } = useTranslation();
  const data = slice.data;

  const { rows, categories } = useMemo(() => buildMixModel(data, t), [data, t]);
  const yukseklik = Math.max(120, rows.length * 34 + 20);

  return (
    <DashboardCard
      cardKey="proje-kategori"
      title={t('expenseDashboard.projects.mixTitle')}
      meta={t('expenseDashboard.projects.windowMeta')}
      span={6}
      spanMd={6}
      loading={slice.loading}
      empty={rows.length === 0}
      emptyText={t('expenseDashboard.projects.empty')}
    >
      <div>
        <ResponsiveContainer width="100%" height={yukseklik}>
          <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }} barCategoryGap={8}>
            <XAxis type="number" domain={[0, 100]} hide />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#4B5563' }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(2, 145, 239, 0.05)' }}
              formatter={(value, name, item) => {
                const kategori = categories.find((c) => c.id === item?.dataKey);
                const tutar = item?.payload?.[`__tutar_${item?.dataKey}`];
                return [`%${value} · ${formatTry(tutar)}`, kategori?.name || name];
              }}
            />
            {categories.map((c, i) => (
              <Bar
                key={c.id}
                dataKey={c.id}
                name={c.name}
                stackId="kategori"
                fill={categoryColors.get(c.id) || CATEGORY_FOLD_COLOR}
                radius={i === categories.length - 1 ? [0, 4, 4, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        <LegendRow>
          {categories.map((c) => (
            <LegendItem key={c.id}>
              <Swatch $color={categoryColors.get(c.id) || CATEGORY_FOLD_COLOR} />
              {c.name}
            </LegendItem>
          ))}
        </LegendRow>
      </div>
    </DashboardCard>
  );
}
